class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    /* iOS SCROLL LOCK (2026-08-13, from a mobile recording — same fix as the
       menu drawer in header-drawer.liquid). iOS Safari ignores
       body{overflow:hidden}: swipes inside the open cart drawer panned and
       rubber-banded the DOCUMENT behind it. While open, swallow any touchmove
       that isn't inside a drawer scroller that actually overflows. */
    document.documentElement.classList.add('sx-drawer-lock');
    this.boundTouchGuard =
      this.boundTouchGuard ||
      ((e) => {
        let node = e.target;
        while (node && node !== document.documentElement) {
          if (node.nodeType === 1 && this.contains(node)) {
            const st = getComputedStyle(node);
            if (/(auto|scroll)/.test(st.overflowY) && node.scrollHeight > node.clientHeight + 1) return;
          }
          node = node.parentElement;
        }
        e.preventDefault();
      });
    document.addEventListener('touchmove', this.boundTouchGuard, { passive: false });
  }

  close(evt) {
    /* 2026-10-14: the drawer opens at the tap and slides in over the button that opened it. A quick second
       tap (~130 ms) on a left-hand Add to cart lands on the overlay before the panel covers it, and used to
       close the drawer for good. Overlay taps in the first 500 ms after a pending open are ignored (window.event
       covers the sections' own overlay handlers, which call close() with no event). */
    const ev = evt || window.event;
    if (ev && ev.type === 'click' && ev.target && ev.target.id === 'CartDrawer-Overlay' && Date.now() < (this._ovGuardUntil || 0)) return;
    /* PERF 2026-10-13: closing while an add is pending is remembered, so the render that lands
       afterwards updates the drawer and the bubble but doesn't slide it back in. */
    if (this.classList.contains('is-pending')) this._userClosed = true;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('sx-drawer-lock');
    if (this.boundTouchGuard) document.removeEventListener('touchmove', this.boundTouchGuard);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* PERF 2026-10-13 (cart speed). The drawer used to appear only after the whole add chain
     (stamp -> add.js -> discount update.js -> two section GETs), 2.1-2.9 s after the tap. Now the
     add handlers call beginPending() at the tap: the drawer slides in at once with the cart as it
     is, a text-free skeleton row on top, a spinner over the total, and Check out / the wallets /
     the line controls un-tappable (cart-drawer.is-pending, trc-cart-drawer.css). No price is
     printed before Shopify's own render lands, so there's no wrong-total flash. renderSections()
     swaps in that render (from the add or update response, market root, wallets included);
     failPending() puts the drawer back as it was. window.__cartBusy holds the discount manager
     (theme.liquid) until the add has finished, so it no longer sends its duplicate update.js. */
  beginPending(hint) {
    this._pendSeq = (this._pendSeq || 0) + 1;
    window.__cartBusy = Date.now();
    this._userClosed = false;
    this.classList.add('is-pending');
    this.classList.remove('is-empty');
    const items = this.querySelector('cart-drawer-items');
    if (items) items.classList.remove('is-empty');
    const box = this.querySelector('#CartDrawer-CartItems');
    if (box && !box.querySelector('.cart-pending-row')) {
      const row = document.createElement('div');
      row.className = 'cart-pending-row';
      row.setAttribute('aria-hidden', 'true');
      const tile = document.createElement('span');
      const lines = document.createElement('span');
      lines.className = 'cart-pending-lines';
      /* 2026-11-03 (add-to-cart speed): Shopify's write takes 1.2-2.2 s, so the row shows the item being added
         (image, name, size) from the page at the tap. The price stays a shimmer until Shopify's render lands. */
      const it = this.pendingItem(hint);
      if (it) {
        row.classList.add('is-item');
        if (it.img) {
          const im = document.createElement('img');
          im.src = it.img;
          im.alt = '';
          tile.append(im);
        }
        const t = document.createElement('b');
        t.textContent = it.title;
        const s = document.createElement('span');
        s.textContent = it.spec || '';
        lines.append(t, s);
        const p = document.createElement('i');
        p.className = 'cart-pending-price';
        row.append(tile, lines, p);
      } else {
        lines.append(document.createElement('i'), document.createElement('i'));
        row.append(tile, lines);
      }
      box.prepend(row);
    }
    clearTimeout(this._pendTimer);
    /* watchdog: a chain that never finishes still ends on the real cart */
    this._pendTimer = setTimeout(() => {
      if (this.classList.contains('is-pending') && typeof window.cartOpenFromGet === 'function') window.cartOpenFromGet();
    }, 12000);
    if (!this.classList.contains('active')) {
      this._ovGuardUntil = Date.now() + 500;
      this.open(hint && hint.trigger);
    }
  }

  /* What the pending row shows: hint.item if the caller passes one, else a drawer suggestion card, else the
     PDP's selected variant (a pack shows the pack label). Null = the text-free skeleton, as before. */
  pendingItem(hint) {
    try {
      if (hint && hint.item && hint.item.title) return hint.item;
      const trig = hint && hint.trigger;
      const card = trig && trig.closest && trig.closest('.cup-row');
      if (card) {
        const im = card.querySelector('.cup-img img');
        const sz = card.querySelector('select.cup-sz');
        const o = sz && sz.options[sz.selectedIndex];
        return { title: (card.querySelector('.cup-t') || {}).textContent.trim(), spec: o ? o.text.trim() : '', img: im && (im.currentSrc || im.src) };
      }
      if (!trig || !trig.closest || !trig.closest('form[action*="/cart/add"]')) return null;
      const p = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
      const idEl = document.querySelector('[data-gh3-id]');
      const h1 = document.querySelector('h1');
      if (!p || !idEl || !h1) return null;
      const v = (p.variants || []).find((x) => String(x.id) === String(idEl.value));
      const tier = document.querySelector('input[name="gh3bnd-tier"]:checked');
      const label = document.querySelector('[data-sticky-label]');
      let spec = v ? v.public_title || '' : '';
      if (tier && parseInt(tier.value, 10) > 1 && label && label.textContent.trim()) spec = label.textContent.trim();
      const im = document.querySelector('.gh2__stage img');
      return { title: h1.textContent.trim(), spec, img: im && (im.currentSrc || im.src) };
    } catch (e) {
      return null;
    }
  }

  renderSections(sections, opts) {
    const o = opts || {};
    const parse = (h) => new DOMParser().parseFromString(h, 'text/html');
    const html = sections && sections['cart-drawer'];
    const doc = html ? parse(html) : null;
    const nd = doc && doc.querySelector('#CartDrawer');
    const cd = document.getElementById('CartDrawer');
    if (!nd || !cd) return false;
    cd.innerHTML = nd.innerHTML;
    const bubbleHtml = sections['cart-icon-bubble'];
    const nb = bubbleHtml && parse(bubbleHtml).querySelector('.shopify-section');
    const cb = document.getElementById('cart-icon-bubble');
    if (nb && cb) cb.innerHTML = nb.innerHTML;
    const src = doc.querySelector('cart-drawer');
    this.classList.toggle('is-empty', !!(src && src.classList.contains('is-empty')));
    const wasPending = this.classList.contains('is-pending');
    this.classList.remove('is-pending');
    clearTimeout(this._pendTimer);
    window.__cartBusy = 0;
    const ov = this.querySelector('#CartDrawer-Overlay');
    if (ov) ov.addEventListener('click', this.close.bind(this));
    if (this.classList.contains('active')) {
      const inner = this.querySelector('.drawer__inner');
      if (typeof trapFocus === 'function' && inner) trapFocus(this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') || cd : cd, inner);
    } else if (o.open !== false && !(wasPending && this._userClosed)) {
      this.open();
    }
    return true;
  }

  failPending(close) {
    const row = this.querySelector('.cart-pending-row');
    if (row) row.remove();
    this.classList.remove('is-pending');
    clearTimeout(this._pendTimer);
    window.__cartBusy = 0;
    const has = !!this.querySelector('#CartDrawer .cart-item, #CartDrawer .cart-item--membership-banner');
    this.classList.toggle('is-empty', !has);
    const items = this.querySelector('cart-drawer-items');
    if (items) items.classList.toggle('is-empty', !has);
    if (close !== false) this.close();
  }
}

customElements.define('cart-drawer', CartDrawer);

/* PERF 2026-10-13 (cart speed). Fallback open, same as the sections' own trcOpenCartDrawer /
   gh3bndOpenDrawer: render the drawer + bubble from the MARKET ROOT (never /cart: a drawer
   rendered from /cart has no express wallets, 2026-08-24), here as ONE ?sections= GET. Used
   when an add's write came back without sections, or the follow-up write failed. */
window.cartOpenFromGet = function () {
  const base = (window.routes && window.routes.cart_url) || '/cart';
  const root = base.replace(/cart\/?$/, '');
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof drawer.renderSections !== 'function') {
    window.location.href = base;
    return Promise.resolve();
  }
  return fetch(root + '?sections=cart-drawer,cart-icon-bubble')
    .then((r) => r.json())
    .then((s) => {
      if (!drawer.renderSections(s)) throw new Error('no drawer section');
    })
    .catch(() => {
      drawer.failPending(false);
      if (!drawer.classList.contains('active')) drawer.open();
    });
};

/* PERF 2026-10-13 (cart speed). One add path for every drawer opener (gh3 single + pack, normal
   and ad view, Bundle this, quick adds). Shopify serialises writes on one cart, so they stay in
   series (8 Sep note), but the drawer now renders from the LAST write's own response
   (Section Rendering: sections + sections_url = market root, which keeps the wallets) instead of
   two more GETs:
     nothing else to set  -> add.js {items, sections}                          1 write, 0 GETs
     code / attribute     -> add.js {items}, then update.js {discount?, attributes?, sections}
   The add carries no sections in the second case, so a pre-discount price is never rendered.
   o = {items, code, attributes, source, variantId}; code null = leave the discount alone, '' = clear it.
   A rejected add (422) throws with Shopify's
   description and e.status; a network failure on add.js throws without status. Anything that
   goes wrong AFTER a successful add never reaches the caller (it falls back to the GET open), so
   a caller's form.submit() fallback can't post the item a second time. Resolves after the render. */
window.cartCommit = function (o) {
  const base = (window.routes && window.routes.cart_url) || '/cart';
  const root = base.replace(/cart\/?$/, '');
  const S = 'cart-drawer,cart-icon-bubble';
  const more = o.code != null || !!o.attributes;
  const post = (u, b) =>
    fetch(u, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(b) });
  window.__cartBusy = Date.now();
  const addBody = more ? { items: o.items } : { items: o.items, sections: S, sections_url: root };
  return post(base + '/add.js', addBody)
    .then((r) =>
      r.json().catch(() => ({})).then((j) => {
        if (!r.ok) {
          const e = new Error((j && j.description) || 'cart add rejected');
          e.status = r.status;
          e.description = j && j.description;
          throw e;
        }
        return j;
      })
    )
    .then((added) => {
      const u = { sections: S, sections_url: root };
      if (o.code != null) u.discount = o.code;
      if (o.attributes) u.attributes = o.attributes;
      /* 2026-10-17: o.pre = the cart read just before the add (gh3 PDP). If add.js was applied twice (a dropped
         connection resent it), its answer shows the line at double; window.__gh3Fix (gh3 sections) returns the
         line keys to put back, and they ride the next write, so the drawer never renders the doubled cart. */
      const fix = o.pre && typeof window.__gh3Fix === 'function' ? window.__gh3Fix(o.pre, o.items, added) : null;
      if (fix) u.updates = fix;
      const tail = (more || fix)
        ? post(base + '/update.js', u).then((r) => {
            if (!r.ok) throw new Error('update ' + r.status);
            return r.json();
          })
        : Promise.resolve(added);
      return tail
        .then((last) => {
          const d = document.querySelector('cart-drawer');
          if (!(d && typeof d.renderSections === 'function' && last && last.sections && d.renderSections(last.sections))) return window.cartOpenFromGet();
        })
        .catch(() => window.cartOpenFromGet())
        .then(() => {
          window.__cartBusy = 0;
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined')
              publish(PUB_SUB_EVENTS.cartUpdate, { source: o.source || 'product-form', productVariantId: o.variantId, cartData: added, drawerRendered: true });
          } catch (e) {}
          return added;
        });
    })
    .catch((e) => {
      window.__cartBusy = 0;
      throw e;
    });
};

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* 2026-11-03 (desktop drawer): the suggestion row shows two whole cards; when there are more, prev/next arrows
   scroll it card by card. Re-applied after every drawer re-render. Phones keep the swipe row, no arrows. */
(function () {
  const mq = window.matchMedia('(min-width: 750px)');
  const icon = (d) =>
    '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="' +
    (d < 0 ? 'M7.5 2 3.5 6l4 4' : 'M4.5 2l4 4-4 4') +
    '" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  function sync(cup) {
    const list = cup.querySelector('.cup-list');
    const btns = cup.querySelectorAll('.cup-nav');
    if (!list || btns.length < 2) return;
    const top = list.offsetTop + list.offsetHeight / 2;
    const over = mq.matches && list.scrollWidth > list.clientWidth + 2;
    btns.forEach((b) => (b.style.top = top + 'px'));
    btns[0].hidden = !over || list.scrollLeft <= 2;
    btns[1].hidden = !over || list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
  }
  function decorate() {
    document.querySelectorAll('#CartDrawer .cup').forEach((cup) => {
      const list = cup.querySelector('.cup-list');
      if (!list) return;
      if (!cup.querySelector('.cup-nav')) {
        [-1, 1].forEach((d) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'cup-nav ' + (d < 0 ? 'cup-nav--prev' : 'cup-nav--next');
          b.setAttribute('aria-label', d < 0 ? 'Previous' : 'Next');
          b.hidden = true;
          b.innerHTML = icon(d);
          b.addEventListener('click', () => {
            const row = list.querySelector('.cup-row');
            const step = row ? row.getBoundingClientRect().width + 10 : list.clientWidth;
            list.scrollBy({ left: d * step, behavior: 'smooth' });
          });
          cup.append(b);
        });
        list.addEventListener('scroll', () => sync(cup), { passive: true });
      }
      sync(cup);
    });
  }
  let t = null;
  const soon = () => {
    clearTimeout(t);
    t = setTimeout(decorate, 30);
  };
  function hook() {
    const d = document.querySelector('cart-drawer');
    if (!d) return;
    new MutationObserver((muts) => {
      if (muts.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && !n.classList.contains('cup-nav')))) soon();
    }).observe(d, { childList: true, subtree: true });
    d.addEventListener('transitionend', soon);
    decorate();
  }
  if (mq.addEventListener) mq.addEventListener('change', soon);
  window.addEventListener('resize', soon);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hook);
  else hook();
})();
